'use client'

import type { FC } from 'react'
import { useState } from 'react'
import { useCallback } from 'react'
import styles from './ShareLocationItem.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import {
  Button,
  ButtonSize,
  ButtonVariants,
  IconBlob,
  IconBlobType,
  IconSize,
  ListItem,
  LocationArrowIcon,
  Tag,
  TagStyle,
  TagVariant,
  useSnackbar,
} from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'

export const ShareLocationItem: FC = () => {
  const { t } = useLocalisation()
  const { showInfo } = useSnackbar()
  const {
    deviceCurrentLocation: { deviceLocation, isLocationBeingResolved, requestLocation },
  } = useAddressBookContext()
  const [userClicked, setUserClicked] = useState(false)
  const isShareButtonLoading = isLocationBeingResolved && userClicked

  const showSnackBar = useCallback(() => {
    showInfo(
      `${t('address-list.location-modal.title')}. ${t('address-list.location-modal.description')}.`,
      { icon: <IconBlob icon={LocationArrowIcon} type={IconBlobType.critical} /> }
    )
  }, [showInfo, t])

  const handleOnClick = useCallback(() => {
    setUserClicked(true)
    if (deviceLocation?.geolocationPermission === 'denied') {
      showSnackBar()
    } else {
      requestLocation()
    }
  }, [deviceLocation, showSnackBar, requestLocation])

  const trailingContent = (
    <Button
      aria-label={t('address-list-share-location.button')}
      loading={isShareButtonLoading}
      size={ButtonSize.small}
      variant={ButtonVariants.secondary}
      onClick={handleOnClick}
    >
      {t('address-list-share-location.button')}
    </Button>
  )

  return (
    <ListItem trailingContent={trailingContent} withDivider={true}>
      <Tag
        className={styles.tag}
        label={t('address-list-share-location.tag')}
        style={TagStyle.positive}
        variant={TagVariant.secondary}
      />
      <div className={styles.shareLocationItem} data-testid="share-location-item">
        <IconBlob icon={LocationArrowIcon} size={IconSize.medium} type={IconBlobType.positive} />
        <div>
          <p className={styles.title}>{t('address-list-share-location.title')}</p>
          <p className={styles.subtitle}>{t('address-list-share-location.subtitle')}</p>
        </div>
      </div>
    </ListItem>
  )
}
